import { GlobalInboundRollbackResultItem } from '@/console/api/types'
import { Callout } from '@/console/ui/Callout'
import { DataTable, DataTableRow } from '@/console/ui/DataTable'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { Stack } from '@/console/ui/Stack'
import { DocumentNumberSelection, GlobalRollbackFlow } from './GlobalRollbackFlow'
import { OutcomeCounts, RESULT_COLUMNS, ReasonSummary, isMutedResult, resultCells } from './GlobalRollbackResultCells'
import { needsMwmsCancelConfirmation } from './globalRollback'
import styles from './GlobalRollback.module.css'

const MWMS_CANCEL_CONFIRM_COLUMN = { key: 'mwmsCancelConfirm', label: 'mWMS 취소 확인' }

/** 실행이 끝나면 확인 목록을 지우므로, 실행 중에 체크한 값이 조용히 사라지지 않게 잠근다. */
const MwmsCancelConfirmCheck = ({
  documentNumber,
  confirmation,
  disabled,
}: {
  documentNumber: string
  confirmation: DocumentNumberSelection
  disabled: boolean
}): JSX.Element => (
  <label className={styles.check}>
    <input
      type="checkbox"
      checked={confirmation.documentNumbers.includes(documentNumber)}
      disabled={disabled}
      onChange={() => confirmation.toggle(documentNumber)}
    />
    <span>mWMS 화면에서 취소를 확인했다</span>
  </label>
)

const MwmsCanceledDatabaseFailedGuide = (): JSX.Element => (
  <Callout tone="crit">
    mWMS 는 취소됐지만 MOMS 는 그대로인 문서가 있어요. mWMS 화면에서 입고취소가 끝난 것을 확인한 문서만 체크하고, 입력에 그 문서를 둔 채 다시
    판정하세요. 판정 결과의 mWMS 취소가 “운영자 확인으로 건너뜀”으로 바뀐 문서만 실행할 수 있어요. mWMS 에 ASN 이 살아 있는데 체크하면 재확정 때 ASN
    이 중복 생성돼요.
  </Callout>
)

const toExecutionRows = (
  results: GlobalInboundRollbackResultItem[],
  confirmation: DocumentNumberSelection,
  confirmationLocked: boolean,
): DataTableRow[] =>
  results.map((item) => ({
    key: item.inboundDocumentNumber,
    muted: isMutedResult(item),
    cells: {
      ...resultCells(item),
      mwmsCancelConfirm: needsMwmsCancelConfirmation(item) ? (
        <MwmsCancelConfirmCheck documentNumber={item.inboundDocumentNumber} confirmation={confirmation} disabled={confirmationLocked} />
      ) : null,
    },
  }))

/** 실행 결과. `FAILED(MWMS_CANCELED_DB_FAILED)` 행에만 mWMS 취소 확인 체크를 둔다 — 건너뛰기 목록의 유일한 입력 경로다(§6.4). */
export const GlobalRollbackExecution = ({ flow }: { flow: GlobalRollbackFlow }): JSX.Element | null => {
  const results = flow.executionResults
  if (!results) {
    return null
  }

  const hasMwmsCanceledDatabaseFailed = results.some(needsMwmsCancelConfirmation)
  return (
    <Stack>
      <SectionHeading level={3} title="실행 결과" count={`${results.length}건`} />
      <OutcomeCounts results={results} />
      <ReasonSummary results={results} />
      {hasMwmsCanceledDatabaseFailed ? <MwmsCanceledDatabaseFailedGuide /> : null}
      <DataTable
        maxHeight={480}
        stickyHeader
        columns={hasMwmsCanceledDatabaseFailed ? [...RESULT_COLUMNS, MWMS_CANCEL_CONFIRM_COLUMN] : RESULT_COLUMNS}
        rows={toExecutionRows(results, flow.mwmsCancelConfirmation, flow.requestInFlight)}
      />
    </Stack>
  )
}
